import React, { useState } from 'react';
import { Box, IconButton, Popover, Tooltip } from '@material-ui/core';
import EmojiIcon from '../../../assets/emojis/emoji-icon.svg';
import EmojiClappingHands from '../../../assets/emojis/emoji_clapping_hands.svg';
import EmojiCryingFace from '../../../assets/emojis/emoji_crying_face.svg';
import EmojiGrinningFace from '../../../assets/emojis/emoji_grinning_face.svg';
import EmojiHeartEyes from '../../../assets/emojis/emoji_heart_eyes.svg';
import EmojiOpenMouthFace from '../../../assets/emojis/emoji_open_mouth_face.svg';
import EmojiRedHeart from '../../../assets/emojis/emoji_red_heart.svg';
import EmojiSmilingSunglasses from '../../../assets/emojis/emoji_smiling_sunglasses.svg';

export const REACTION_EMOJIS: { value: string; icon: string }[] = [
  { value: 'clap', icon: EmojiClappingHands },
  { value: 'heart', icon: EmojiRedHeart },
  { value: 'grinning', icon: EmojiGrinningFace },
  { value: 'sunglasses', icon: EmojiSmilingSunglasses },
  { value: 'heart_eyes', icon: EmojiHeartEyes },
  { value: 'cry', icon: EmojiCryingFace },
  { value: 'open_mouth', icon: EmojiOpenMouthFace },
];

const iconFor = (emoji: string) => REACTION_EMOJIS.find((e) => e.value === emoji)?.icon;

type ReactionRow = { emoji: string; volunteerId: string };

interface ChipsProps {
  reactions: ReactionRow[];
  currentUserId: string;
  onToggle: (emoji: string) => void;
  alignRight?: boolean;
}

export const ReactionChips = ({ reactions, onToggle }: ChipsProps) => {
  const safeReactions = reactions ?? [];
  const grouped = safeReactions.reduce<Record<string, ReactionRow[]>>((acc, r) => {
    acc[r.emoji] = [...(acc[r.emoji] || []), r];
    return acc;
  }, {});

  const order = REACTION_EMOJIS.map((e) => e.value).filter((v) => grouped[v]?.length);

  if (order.length === 0) return null;

  return (
    <Box
      display='flex'
      alignItems='center'
      style={{
        gap: 3,
        position: 'absolute',
        bottom: -10,
        right: 8,
        zIndex: 1,
      }}
    >
      {order.map((emoji) => {
        const group = grouped[emoji];
        const icon = iconFor(emoji);
        if (!icon) return null;
        return (
          <Box
            key={emoji}
            onClick={() => onToggle(emoji)}
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: group.length > 1 ? '2px 8px 2px 3px' : 2,
              borderRadius: 999,
              border: '1px solid #e8e8e8',
              background: '#fafafa',
              fontSize: 11,
              color: '#646464',
              fontWeight: 600,
              lineHeight: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            <img src={icon} alt={emoji} style={{ width: 15, height: 15, display: 'block' }} />
            {group.length > 1 && <span>{group.length}</span>}
          </Box>
        );
      })}
    </Box>
  );
};

interface PickerProps {
  onToggle: (emoji: string) => void;
  className?: string;
}

export const ReactionPickerButton = ({ onToggle, className }: PickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title='Add reaction'>
        <IconButton
          size='small'
          disableRipple
          className={className}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <img src={EmojiIcon} alt='' style={{ width: 18, height: 18 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box display='flex' alignItems='center' p={0.5}>
          {REACTION_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji.value}
              size='small'
              onClick={() => {
                onToggle(emoji.value);
                setAnchorEl(null);
              }}
              style={{ padding: 4 }}
            >
              <img src={emoji.icon} alt={emoji.value} style={{ width: 22, height: 22 }} />
            </IconButton>
          ))}
        </Box>
      </Popover>
    </>
  );
};
